import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LogService, { LOG_ACTIONS } from '../services/LogService';
import cubDataDefault, { CUB_EXTRAS, AREA_COEFICIENTES, TIPO_LABELS } from '../data/cubData';

function loadCubData() {
  try {
    const stored = localStorage.getItem('orcai_cub_data');
    if (stored) return JSON.parse(stored);
  } catch { /* fallback */ }
  return cubDataDefault;
}

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Itens de engenharia/construção exibidos nesta página
const EXTRAS_ENGENHARIA = Object.entries(CUB_EXTRAS)
  .filter(([, def]) => def.categoria === 'engenharia');

// Itens de acabamento (vêm pré-selecionados da página anterior)
const EXTRAS_ACABAMENTO_KEYS = Object.entries(CUB_EXTRAS)
  .filter(([, def]) => def.categoria === 'acabamento')
  .map(([key]) => key);

function OrcamentoCub() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cubValues = useMemo(() => loadCubData(), []);

  const [nomeProjeto, setNomeProjeto] = useState('');
  const [estado, setEstado] = useState('');
  const [tipoProjeto, setTipoProjeto] = useState('');
  const [areaConstruida, setAreaConstruida] = useState('');
  const [areaGaragem, setAreaGaragem] = useState('');
  const [areaVaranda, setAreaVaranda] = useState('');
  const [areaSubsolo, setAreaSubsolo] = useState('');
  const [extras, setExtras] = useState(() => {
    const init = {};
    Object.keys(CUB_EXTRAS).forEach((k) => {
      const def = CUB_EXTRAS[k];
      init[k] = { checked: false, qty: def.tipo === 'unitario' ? 1 : def.tipo === 'por_m2' ? '' : 0 };
    });
    return init;
  });
  const [bdi, setBdi] = useState(20);
  const [padraoAcabamento, setPadraoAcabamento] = useState(null);
  const [comodosDaPlanta, setComodosDaPlanta] = useState([]);
  const [observacoesPlanta, setObservacoesPlanta] = useState([]);
  const [peDireitoPlanta, setPeDireitoPlanta] = useState('2,80m');
  const [coberturaPlanta, setCoberturaPlanta] = useState(null);

  useEffect(() => {
    const fromAnalise = searchParams.get('from') === 'analise';
    if (fromAnalise) {
      try {
        const stored = localStorage.getItem('orcai_cub_prefill');
        if (stored) {
          const pf = JSON.parse(stored);
          if (pf.estado) setEstado(pf.estado);
          if (pf.tipoProjeto) setTipoProjeto(pf.tipoProjeto);
          if (pf.areaConstruida) setAreaConstruida(pf.areaConstruida);
          if (pf.areaGaragem) setAreaGaragem(pf.areaGaragem);
          if (pf.areaVaranda) setAreaVaranda(pf.areaVaranda);
          if (pf.areaSubsolo) setAreaSubsolo(pf.areaSubsolo);
          if (pf.nomeProjeto) setNomeProjeto(pf.nomeProjeto);
          if (pf.padraoAcabamento) setPadraoAcabamento(pf.padraoAcabamento);
          if (pf.comodos) setComodosDaPlanta(pf.comodos);
          if (pf.observacoes) setObservacoesPlanta(pf.observacoes);
          if (pf.peDireito) setPeDireitoPlanta(pf.peDireito);
          if (pf.cobertura) setCoberturaPlanta(pf.cobertura);
          // Pre-check extras matching special items
          if (pf.itensEspeciais && pf.itensEspeciais.length > 0) {
            setExtras(prev => {
              const updated = { ...prev };
              pf.itensEspeciais.forEach(id => {
                if (updated[id]) updated[id] = { ...updated[id], checked: true };
              });
              return updated;
            });
          }
          localStorage.removeItem('orcai_cub_prefill');
          return;
        }
      } catch { /* fallback to URL params */ }
    }
    const area = searchParams.get('area');
    if (area && !isNaN(parseFloat(area))) setAreaConstruida(area);
  }, [searchParams]);

  const estadosOrdenados = useMemo(() => {
    return Object.entries(cubValues.estados)
      .map(([sigla, data]) => ({ sigla, nome: data.nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [cubValues]);

  const tiposDisponiveis = useMemo(() => {
    if (!estado || !cubValues.estados[estado]) return [];
    return Object.entries(cubValues.estados[estado].tipos).map(([codigo, data]) => ({
      codigo,
      label: TIPO_LABELS[codigo] || data.descricao,
      valor: data.valor,
    }));
  }, [estado, cubValues]);

  const cubValorSelecionado = useMemo(() => {
    if (!estado || !tipoProjeto) return null;
    return cubValues.estados[estado]?.tipos[tipoProjeto]?.valor || null;
  }, [estado, tipoProjeto, cubValues]);

  const toggleExtra = (key) => {
    setExtras((prev) => ({ ...prev, [key]: { ...prev[key], checked: !prev[key].checked } }));
  };

  const updateExtraQty = (key, value) => {
    setExtras((prev) => ({ ...prev, [key]: { ...prev[key], qty: value } }));
  };

  const canCalculate = estado && tipoProjeto && parseFloat(areaConstruida) > 0;

  // Count pre-selected acabamento items for display
  const acabamentoSelecionados = EXTRAS_ACABAMENTO_KEYS.filter(k => extras[k]?.checked);

  const handleCalculate = () => {
    if (!canCalculate) return;
    const cub = cubValorSelecionado;
    const areaPrincipal = parseFloat(areaConstruida) || 0;
    const garagem = parseFloat(areaGaragem) || 0;
    const varanda = parseFloat(areaVaranda) || 0;
    const subsolo = parseFloat(areaSubsolo) || 0;

    const linhas = [];

    linhas.push({
      item: `Área principal`,
      calculo: `${areaPrincipal.toLocaleString('pt-BR')} m² x 1,00 x ${formatBRL(cub)}`,
      valor: areaPrincipal * cub,
    });

    if (garagem > 0) {
      linhas.push({
        item: `Garagem`,
        calculo: `${garagem.toLocaleString('pt-BR')} m² x 0,50 x ${formatBRL(cub)}`,
        valor: garagem * 0.5 * cub,
      });
    }
    if (varanda > 0) {
      linhas.push({
        item: `Varanda / Sacada`,
        calculo: `${varanda.toLocaleString('pt-BR')} m² x 0,75 x ${formatBRL(cub)}`,
        valor: varanda * 0.75 * cub,
      });
    }
    if (subsolo > 0) {
      linhas.push({
        item: `Subsolo`,
        calculo: `${subsolo.toLocaleString('pt-BR')} m² x 0,50 x ${formatBRL(cub)}`,
        valor: subsolo * 0.5 * cub,
      });
    }

    const subtotalAreas = linhas.reduce((s, l) => s + l.valor, 0);
    const extrasLinhas = [];

    Object.entries(extras).forEach(([key, state]) => {
      if (!state.checked) return;
      const def = CUB_EXTRAS[key];
      let valor = 0;
      let calculo = '';

      if (def.tipo === 'fixo') {
        valor = def.valor;
        calculo = 'Valor estimado';
      } else if (def.tipo === 'unitario') {
        const qty = parseInt(state.qty) || 1;
        valor = def.valor * qty;
        calculo = `${qty} un x ${formatBRL(def.valor)}`;
      } else if (def.tipo === 'percentual') {
        valor = subtotalAreas * def.valor;
        calculo = `${(def.valor * 100).toFixed(0)}% do subtotal CUB`;
      } else if (def.tipo === 'por_m2') {
        const area = parseFloat(state.qty) || areaPrincipal;
        valor = def.valor * area;
        calculo = `${area.toLocaleString('pt-BR')} m² x ${formatBRL(def.valor)}/m²`;
      }

      extrasLinhas.push({ item: def.label, calculo, valor });
    });

    const subtotalExtras = extrasLinhas.reduce((s, l) => s + l.valor, 0);
    const subtotalGeral = subtotalAreas + subtotalExtras;
    const bdiValor = subtotalGeral * (bdi / 100);
    const total = subtotalGeral + bdiValor;

    const areaTotal = areaPrincipal + garagem + varanda + subsolo;

    const res = {
      nomeProjeto: nomeProjeto.trim() || null,
      cubBase: cub,
      areaPrincipal,
      areaTotal,
      tipoLabel: TIPO_LABELS[tipoProjeto] || tipoProjeto,
      estadoNome: cubValues.estados[estado].nome,
      estadoSigla: estado,
      tipoCodigo: tipoProjeto,
      linhasAreas: linhas,
      subtotalAreas,
      linhasExtras: extrasLinhas,
      subtotalExtras,
      subtotalGeral,
      bdiPercent: bdi,
      bdiValor,
      total,
      dataRef: cubValues.data_atualizacao,
      padraoAcabamento: padraoAcabamento || null,
      comodos: comodosDaPlanta,
      observacoes: observacoesPlanta,
      peDireito: peDireitoPlanta,
      cobertura: coberturaPlanta,
    };

    localStorage.setItem('orcai_cub_resultado', JSON.stringify(res));
    navigate('/orcamento/resultado-cub');

    LogService.log(LOG_ACTIONS.CUB_CALCULATE, {
      details: {
        estado,
        tipoProjeto,
        areaConstruida: areaPrincipal,
        areaEquivalente: areaPrincipal + garagem * 0.5 + varanda * 0.75 + subsolo * 0.5,
        total,
        bdi,
      },
    });
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all';

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10 pt-36">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2 break-words">
            Orçamento por CUB — Revisão Final
          </h1>
          <p className="text-muted-foreground">Calcule o custo estimado da obra usando o Custo Unitário Básico (CUB/m²)</p>
        </div>

        {/* Form */}
        <div className="bg-card rounded-2xl shadow-card p-6 md:p-8 mb-6">
          {/* Nome do Projeto */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-1.5">Nome do Projeto (opcional)</label>
            <input
              type="text"
              value={nomeProjeto}
              onChange={(e) => setNomeProjeto(e.target.value)}
              placeholder="Ex: Casa da Praia, Reforma Apto 301..."
              className={inputClass}
            />
          </div>

          {/* Estado e Tipo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Estado</label>
              <select
                value={estado}
                onChange={(e) => { setEstado(e.target.value); setTipoProjeto('');}}
                className={inputClass}
              >
                <option value="">Selecione o estado</option>
                {estadosOrdenados.map((e) => (
                  <option key={e.sigla} value={e.sigla}>{e.nome} ({e.sigla})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Tipo de Projeto</label>
              <select
                value={tipoProjeto}
                onChange={(e) => { setTipoProjeto(e.target.value);}}
                disabled={!estado}
                className={inputClass + ' disabled:opacity-50 disabled:cursor-not-allowed'}
              >
                <option value="">Selecione o tipo</option>
                {tiposDisponiveis.map((t) => (
                  <option key={t.codigo} value={t.codigo}>{t.codigo} - {t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* CUB selecionado */}
          {cubValorSelecionado && (
            <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-accent font-medium">CUB {tipoProjeto} - {cubValues.estados[estado]?.nome}</p>
                <p className="text-xl font-bold text-secondary">{formatBRL(cubValorSelecionado)}<span className="text-sm font-normal text-accent"> /m²</span></p>
              </div>
            </div>
          )}

          {/* Areas */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Áreas (m²)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Área construída total *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={areaConstruida}
                  onChange={(e) => { setAreaConstruida(e.target.value);}}
                  placeholder="Ex: 150"
                  className={inputClass}
                />
              </div>
              {Object.entries(AREA_COEFICIENTES).map(([key, def]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    {def.label} <span className="text-muted-foreground">(coef. {def.coeficiente.toFixed(2).replace('.', ',')})</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={key === 'garagem' ? areaGaragem : key === 'varanda' ? areaVaranda : areaSubsolo}
                    onChange={(e) => {
                      if (key === 'garagem') setAreaGaragem(e.target.value);
                      else if (key === 'varanda') setAreaVaranda(e.target.value);
                      else setAreaSubsolo(e.target.value);
                    }}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Itens extras - apenas engenharia/construção */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Itens Extras — Construção e Engenharia
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {EXTRAS_ENGENHARIA.map(([key, def]) => (
                <div
                  key={key}
                  className={`border rounded-xl p-3 transition-all cursor-pointer ${
                    extras[key]?.checked ? 'border-accent/30 bg-accent/10' : 'border-border bg-card hover:border-border'
                  }`}
                  onClick={() => toggleExtra(key)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      extras[key]?.checked ? 'bg-accent border-accent' : 'border-border'
                    }`}>
                      {extras[key]?.checked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{def.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {def.tipo === 'fixo' && formatBRL(def.valor)}
                        {def.tipo === 'unitario' && `${formatBRL(def.valor)} / unidade`}
                        {def.tipo === 'percentual' && `${(def.valor * 100).toFixed(0)}% do custo`}
                        {def.tipo === 'por_m2' && `${formatBRL(def.valor)} / m²`}
                      </p>
                    </div>
                  </div>
                  {extras[key]?.checked && def.tipo === 'unitario' && (
                    <div className="mt-2 ml-8" onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-muted-foreground">Quantidade:</label>
                      <input
                        type="number"
                        min="1"
                        value={extras[key].qty}
                        onChange={(e) => { updateExtraQty(key, e.target.value);}}
                        className="ml-2 w-20 px-2 py-1 text-sm rounded-lg border border-border bg-card focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  )}
                  {extras[key]?.checked && def.tipo === 'por_m2' && (
                    <div className="mt-2 ml-8" onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-muted-foreground">Área (m²):</label>
                      <input
                        type="number"
                        min="0"
                        value={extras[key].qty}
                        onChange={(e) => { updateExtraQty(key, e.target.value);}}
                        placeholder={areaConstruida || '0'}
                        className="ml-2 w-24 px-2 py-1 text-sm rounded-lg border border-border bg-card focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Show pre-selected acabamento items from page 2 */}
            {acabamentoSelecionados.length > 0 && (
              <div className="mt-4 p-3 bg-muted/50 rounded-xl border border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase">Itens de acabamento selecionados na etapa anterior:</p>
                <div className="flex flex-wrap gap-2">
                  {acabamentoSelecionados.map(k => (
                    <span key={k} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-lg font-medium">
                      {CUB_EXTRAS[k].label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* BDI */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              BDI - Benefícios e Despesas Indiretas
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <input
                  type="range"
                  min="15"
                  max="30"
                  step="1"
                  value={bdi}
                  onChange={(e) => { setBdi(parseInt(e.target.value));}}
                  className="bdi-slider w-full h-2 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              <div className="bg-accent/10 border border-accent/30 rounded-lg px-3 py-1.5 min-w-[60px] text-center">
                <span className="text-lg font-bold text-secondary">{bdi}%</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">Inclui lucro, administração, impostos e contingências (típico: 15% a 30%)</p>
          </div>

          {/* Botao */}
          <button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="w-full py-4 text-base font-bold text-white bg-gradient-cta shadow-cta rounded-xl cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Calcular Orçamento
          </button>
        </div>

        {/* BDI Slider CSS */}
        <style>{`
          .bdi-slider {
            background: linear-gradient(to right, var(--color-primary, #3b82f6) 0%, var(--color-primary, #3b82f6) ${((bdi - 15) / 15) * 100}%, #e2e8f0 ${((bdi - 15) / 15) * 100}%, #e2e8f0 100%);
          }
          .bdi-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: var(--color-primary, #3b82f6);
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          }
          .bdi-slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: var(--color-primary, #3b82f6);
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          }
          .bdi-slider::-webkit-slider-runnable-track {
            height: 8px;
            border-radius: 4px;
          }
          .bdi-slider::-moz-range-track {
            height: 8px;
            border-radius: 4px;
            background: #e2e8f0;
          }
        `}</style>

      </main>
      <Footer />
    </div>
  );
}

export default OrcamentoCub;
